
import { format } from 'date-fns'
import { defineComponent, PropType } from 'vue'
import { mapState } from 'vuex'
import AppointmentService from '../common/appointmentService'
import AppointmentScheduler from '../components/AppointmentScheduler.vue'
import Appointment from '../models/appointment'

import addDays from 'date-fns/addDays'
import AvailabilitySlot from '../models/availabilitySlot'
import AppointmentSlotSelection from '../components/AppointmentSlotSelection.vue'
import { cloneDeep } from 'lodash'
import UserReassign from '../models/userReassign'
import { AxiosError } from 'axios'
import { ProposalNumberType } from '../models/proposalType'

export default defineComponent({
  name: 'Schedule',

  props: {
    appointment: {
      type: Object as PropType<Appointment>,
      required: true
    },
    visible: {
      type: Boolean,
      required: true,
      default: false
    }
  },

  data () {
    return {
      isActive: true,

      /** List of scheduled appointments for the current user */
      appointments: [] as Array<Appointment>,
      selectedAppointment: null as unknown as Appointment,
      reviewDeadline: null as unknown as Date,
      shouldShowRescheduler: false,

      availableReviewers: [] as Array<UserReassign>,
      /** Indicates if the scheduler interface should be shown */
      shouldShowScheduler: false
    }
  },

  computed: {
    ...mapState(['userId']),

    /**
     * Sorts available reviewers by workload
     */
    sortedAvailableReviewers (): Array<UserReassign> {
      const reviewers = cloneDeep(this.availableReviewers)
        .sort((a, b) => {
          if (a.workload > b.workload) {
            return 1
          } else if (a.workload < b.workload) {
            return -1
          } else {
            return 0
          }
        })

      return reviewers.filter(r => !r.hasExternalConflicts && r.availableForSlot)
    }
  },

  watch: {
    userId: {
      handler () {
        this.getAppointments()
      },
      immediate: true
    }
  },

  methods: {
    /**
     * The display value for the proposal number formatted to include the
     * proposal number type
     */
    proposalNumberDisplay (appointment: Appointment): string {
      return AppointmentService.formatProposalNumber(appointment)
    },

    /**
     * Sorts the current list of appointments
     */
    sortedAppointments (): Array<Appointment> {
      const appointments = cloneDeep(this.appointments)
        .sort((a, b) => {
          if (a.start > b.start) {
            return 1
          } else if (a.start < b.start) {
            return -1
          } else {
            return 0
          }
        })

      return appointments
    },

    /**
     * Add one to the date, without time it will change it to midnight of the review deadline
     */
    endOfDayDeadline (): Date {
      this.reviewDeadline = addDays(new Date(this.selectedAppointment.reviewDeadline), 1)
      return this.reviewDeadline
    },

    /**
     * Handles an appointment being scheduled
     */
    handleScheduled () {
      setTimeout(() => {
        this.shouldShowScheduler = false
        this.getAppointments()
      }, 3000)
    },

    /**
     * get appointments for the date range
     */
    getAppointments () {
      AppointmentService.getForProjectUser(this.userId, true).then(res => {
        res.data.forEach(app => {
          app.start = new Date(app.start)
          app.end = new Date(app.end)
        })
        this.appointments = res.data
      })
    },

    /**
     * Formats the given date in the specified format
     */
    formatTime (date: Date, formatString: string): string {
      return format(date, formatString)
    },

    formatProposalType (proposalType: ProposalNumberType): string {
      return proposalType === 'QuickProposal' ? 'QP-' : 'PD-'
    },

    /**
     * Cancel selected appointment
     * @param appointemntId
     */
    cancelAppointment (appointemntId: number) {
      if (confirm('Are you sure you want to cancel this appointment?')) {
        AppointmentService.cancelAppointment(appointemntId).then(() => {
          const ind = this.appointments.findIndex(app => app.id === appointemntId)
          this.appointments.splice(ind, 1)
        })
      }
    },

    /**
     * Reschedule selected appointment to another slot
     * @param appointmentId
     */
    rescheduleAppointment (appointmentId: number) {
      const appt = this.appointments.find(app => app.id === appointmentId)
      this.selectedAppointment = appt as Appointment
      this.shouldShowRescheduler = true
    },

    /**
     * reset reschedule form
     */
    resetReschedule () {
      this.shouldShowRescheduler = false
    },

    /**
     * Once a new slot is selected for rescheduling, this will be executed
     * @param slot
     */
    handleSlotSelection (slot: AvailabilitySlot) {
      if (confirm('Are you sure you want to reschedule this appointment until ' + slot.start.toLocaleString() + '?')) {
        const params = {} as any
        // const reviewers = this.getReviewerForReschedule(slot)
        // if (reviewers.length > 0) {
        //   params.reviewerId = reviewers[0].id
        // } else {
        //   params.reviewerId = 0
        // }

        params.startTime = slot.start
        params.appointmentTypeId = slot.appointmentType.id

        if (this.selectedAppointment.appointmentTypeOffering.id !== 0) {
          params.appointmentTypeOfferingId = this.selectedAppointment.appointmentTypeOffering.id
        }
        const allFields = cloneDeep(this.selectedAppointment.intakeFormResponse)
        params.intakeFormResponse = allFields

        AppointmentService.rescheduleAppointment(this.selectedAppointment.id, params, false).then(res => {
          this.resetReschedule()
          this.shouldShowRescheduler = false
          res.data.start = new Date(res.data.start)
          res.data.end = new Date(res.data.end)
          res.data.scheduledOn = new Date(res.data.scheduledOn)
          res.data.reviewDeadline = new Date(res.data.reviewDeadline)
          const ind = this.appointments.findIndex(app => app.id === this.selectedAppointment.id)
          this.appointments.splice(ind, 1, res.data)
          this.isActive = true
        }).catch((err: AxiosError) => {
          if (err.response?.status === 409) {
            alert('No longer available.')
          }
        })
      }
      this.appointments = this.sortedAppointments()
    }

    // getReviewerForReschedule (slot: AvailabilitySlot): Array<UserReassign> {
    //   AppointmentService.reviewerAvailability(slot.appointmentTypeOffering.id, slot.start).then(rev => {
    //     this.availableReviewers = rev.data
    //   })

    //   this.availableReviewers.forEach(rev => {
    //     if (rev.hasExternalConflicts || !rev.availableForSlot) {
    //       const ind = this.availableReviewers.findIndex(ar => ar.id === rev.id)
    //       this.availableReviewers.splice(ind, 1)
    //     }
    //   })

    //   return this.sortedAvailableReviewers
    // }
  },

  components: {
    AppointmentScheduler,
    AppointmentSlotSelection
  }
})
